import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalList, LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/responsible-lending")({
  component: ResponsibleLendingPage,
});

function ResponsibleLendingPage() {
  return (
    <LegalPage
      title="Responsible Lending Policy"
      effective="CREDIKING FINANCIAL SERVICE LIMITED · Effective Date: 20th August, 2026"
    >
      <LegalSection title="1. Purpose">
        <p>
          Crediking Financial Service Limited is committed to responsible, ethical, and sustainable
          lending practices. This Policy ensures we assess customers’ ability to repay loans in a
          sustainable manner, avoid reckless or predatory lending, provide clear information, and
          treat customers fairly — consistent with CBN Consumer Protection Regulations (Responsible
          Lending and Responsible Business Conduct provisions), and (where applicable) FCCPC
          Digital/Electronic/Online/Non-Traditional Consumer Lending Regulations 2025.
        </p>
      </LegalSection>

      <LegalSection title="2. Scope">
        <p>
          Applies to all consumer loan products (including digital, online, or non-traditional
          channels) offered by the Company, its staff, agents, and partners.
        </p>
      </LegalSection>

      <LegalSection title="3. Core Principles">
        <LegalList
          items={[
            <>Lend only what customers can reasonably afford to repay without undue hardship.</>,
            <>Treat customers fairly, with transparency and dignity at every stage.</>,
            <>Prohibit reckless, predatory, or exploitative lending and unethical recovery practices.</>,
            <>Promote financial literacy and responsible borrowing.</>,
            <>
              Comply fully with the Nigeria Data Protection Act 2023 (NDPA) and related regulations
              when processing customer data.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Credit Assessment and Affordability">
        <p>Before granting any credit we will:</p>
        <LegalList
          items={[
            <>
              Assess the customer’s capability to repay in a sustainable manner, taking into account
              income, existing obligations, living expenses, and overall financial circumstances.
            </>,
            <>
              Obtain and review credit history from the Credit Risk Management System (CRMS), licensed
              Credit Bureaux, and other reliable sources.
            </>,
            <>Define clear product suitability criteria and approval authorities in our credit risk procedures.</>,
            <>Decline, vary, or amend applications where appropriate and notify the customer.</>,
            <>Not rely solely on automated decisions without appropriate human oversight where required.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Disclosure and Transparency">
        <p>Before a customer enters into a loan agreement we will:</p>
        <LegalList
          items={[
            <>
              Disclose, in clear, simple, legible English (and other languages where appropriate), all
              material terms: principal, interest rate (and method of calculation), total cost of
              credit, fees, charges, penalties, repayment schedule and amounts, tenure, and any other
              conditions.
            </>,
            <>Ensure information is not misleading or deceptive and is understandable to the average consumer.</>,
            <>Provide a copy of the agreement and key fact statements.</>,
            <>Clearly display rates and charges on our platforms.</>,
            <>Inform customers of circumstances that may affect the contract.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="6. Product Suitability">
        <LegalList
          items={[
            <>We offer products suitable to the customer’s needs and circumstances.</>,
            <>
              We will not compel customers to purchase insurance, valuation, or other third-party
              products from a specific provider as a precondition for a loan. Where such products are
              offered, customers will have a genuine choice (at least three providers where required
              by regulation).
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="7. Customers in Financial Difficulty">
        <p>
          We maintain a clear policy for customers experiencing repayment difficulties (e.g. due to
          illness, job loss, or other genuine hardship):
        </p>
        <LegalList
          items={[
            <>Early detection through monitoring of loan performance.</>,
            <>
              Prompt engagement to discuss alternative repayment options (restructuring, temporary
              relief, etc.) appropriate to the changed circumstances.
            </>,
            <>Fair and considerate treatment.</>,
            <>Credit counselling support to help prevent over-indebtedness.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="8. Debt Recovery">
        <p>Recovery activities will be:</p>
        <LegalList
          items={[
            <>Transparent, courteous, fair, and professional.</>,
            <>Preceded by proper notice of outstanding obligations.</>,
            <>
              Free from harassment, intimidation, public shaming, threats, defamation, unauthorised
              access to contacts, or other abusive tactics prohibited by CBN and FCCPC rules.
            </>,
            <>
              Conducted only by authorised personnel or licensed third parties who adhere to this
              Policy and applicable regulations.
            </>,
          ]}
        />
        <p>
          Foreclosure or enforcement of security (where applicable) will follow due process and
          regulatory requirements.
        </p>
      </LegalSection>

      <LegalSection title="9. Marketing and Sales Conduct">
        <LegalList
          items={[
            <>Marketing will be clear, fair, and not misleading.</>,
            <>No unsolicited advertisements at the customer’s cost.</>,
            <>Staff and agents are trained on responsible lending, ethical conduct, and regulatory requirements.</>,
            <>
              Pre-authorised or automatic lending without proper customer consent and assessment is
              prohibited where restricted by regulation.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="10. Data Protection and Privacy">
        <p>
          All personal data is processed in accordance with the Nigeria Data Protection Act 2023. We
          obtain lawful bases (including consent where required), minimise data collection, protect
          data securely, and respect data subject rights. Unauthorised sharing or misuse of data for
          recovery or other purposes is strictly prohibited. See our{" "}
          <Link to="/privacy" className="text-gold hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </LegalSection>

      <LegalSection title="11. Monitoring, Training, and Governance">
        <LegalList
          items={[
            <>Regular monitoring of lending practices, portfolio quality, and complaint trends.</>,
            <>Staff training on this Policy, affordability assessment, disclosure, and ethical recovery.</>,
            <>Board/Senior Management oversight and periodic review of the Policy.</>,
            <>Records retained as required by law and regulation.</>,
          ]}
        />
      </LegalSection>

      <LegalSection title="12. Customer Responsibilities">
        <p>
          Customers are expected to provide accurate and complete information, use credit
          responsibly, and contact us early if they anticipate repayment difficulties. If you have a
          concern, see our{" "}
          <Link to="/complaints" className="text-gold hover:underline">
            Complaints Policy
          </Link>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
